/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import Modal from '../common/Modal'
import { dateManipulateDay, dateManipulateMonth, diffDays, endOfMonth, format, nowDate, secToHour, secToMinute, secToSec, yesterDay } from '../../../utils/date'
import Holidays from 'date-holidays'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import LoadingSquare from '../common/LoadingSquare'
import { clone } from 'lodash'
import Tips from '../common/Tips'
import { convertHexColor } from '../../../utils/color'
import { PackageBlock, ResponseGetTaskGanttChartDetail } from '../../../states/types/task'
import GanttChartScheduleList from './GanttChartScheduleList'
import SELECT_LIST from '../../constants/taskGanttChartStatus'

interface MemberBase {
  member_id: number
  name: string
  kana: string
  code: string | null
  profile_image: string | null
}

interface Member extends MemberBase {
  status: string | null
  schedule: {
    start_date: string | null
    end_date: string | null
    time: number
  }
  actual: {
    date: string
    time: number
  }[]
}

interface DetailData {
  memberList: MemberBase[]
  labelGroupId?: number
  bgColor?: string
  editable?: boolean
}

interface UpdateParams {
  blockType: string
  id: number
  parentId?: number
  memberIds?: number[]
  startDate: string | null
  endDate: string | null
  time?: number
  status?: string | null
  callback: VoidFunction
}

interface Props {
  closeCallback: VoidFunction
  updateCallback: (params: UpdateParams) => void
  openModalCallback: (params: {blockType: string, id: number, modalType: string, displayType: string, labelGroupId?: number, memberIds?: number[], parentId?: number, assignedMemberIds?: number[]}) => void
  isDisplay: boolean
  displayBlockType: string
  dataList: ResponseGetTaskGanttChartDetail | undefined
  isLoading: boolean
  displayType: string
  assignedMemberIds?: number[]
  detailData: DetailData | undefined
  currentMemberId: number
}

const GanttDetailModal: FC<Props> = (props) => {
  const {
    closeCallback,
    updateCallback,
    openModalCallback,
    isDisplay,
    displayBlockType,
    dataList,
    isLoading,
    displayType,
    assignedMemberIds,
    detailData,
    currentMemberId,
  } = props

  const holidays = new Holidays()
  holidays.init('JP')

  const DAILY_BLOCK_WIDTH = 22
  const WEEKLY_BLOCK_WIDTH = (DAILY_BLOCK_WIDTH * 3) + 2

  const STATUS_WIDTH = 100

  const dateRef = useRef() as React.MutableRefObject<HTMLInputElement>
  const titleRef = useRef() as React.MutableRefObject<HTMLDivElement>

  const [isDisplayWeekly, setIsDisplayWeekly] = useState<boolean>(false)

  const [data, setData] = useState<ResponseGetTaskGanttChartDetail | undefined>(undefined)

  const [addMonthNum, setAddMonthNum] = useState<number>(0)

  const [titleWidth, setTitleWidth] = useState<number>(250)
  const [isMoveDrag, setIsMoveDrag] = useState<boolean>(false)

  const [isStatusLoading, setIsStatusLoading] = useState<boolean>(false)

  const displayDateList = useMemo(() => {
    if (!dataList) return []
    const targetDate = dateManipulateMonth(nowDate("YYYY-MM-01"), addMonthNum)
    if (isDisplayWeekly) {
      const weekList = []
      const startDate = dateManipulateMonth(targetDate, -1)
      const endDate = dateManipulateMonth(targetDate, 3)
      for (let d = clone(new Date(startDate));d <= new Date(endDate);d.setDate(d.getDate() + 1)) {
        if (d.getDay() === 1) {
          weekList.push(format(d, "YYYY-MM-DD"))
        }
      }
      return weekList
    }
    const dateList = []
    const startDate = dateManipulateMonth(targetDate, -1)
    const endDate = endOfMonth(dateManipulateMonth(targetDate, 1))
    for (let d = clone(new Date(startDate));d <= new Date(endDate);d.setDate(d.getDate() + 1)) {
      dateList.push(format(d, "YYYY-MM-DD"))
    }
    return Array.from(new Set(dateList))
  }, [dataList, isDisplayWeekly, addMonthNum])

  const listCount = useMemo(() => (dataList?.members.length || 0) + 1, [dataList])

  const maxMemberActualTime = useMemo(() => {
    if (!dataList?.members) return 0
    return Math.max(...dataList.members.map((v) => Math.max(...v.actual.map((w) => w.time))))
  }, [dataList])

  const maxActualTimeMember = useMemo(() => {
    if (!dataList?.members) return ({ name: "", time: 0 })
    return dataList.members.map((v) => ({ name: v.name, time: v.actual.reduce((s, w) => s + w.time, 0) })).sort((a, b) => b.time - a.time)[0]
  }, [dataList])

  const calendarColor = (day: string, bg = false) => {
    if (Number.isNaN(new Date(day).getTime())) return bg ? "#FFFFFF" : "#666666"
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return bg ? "#fff7f7" : "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return bg ? "#f1f9ff" : "#317AB7"
    }
    return bg ? "#FFFFFF" : "#666666"
  }

  const closeModal = () => {
    closeCallback()
    setAddMonthNum(0)
    setTitleWidth(250)
  }

  const buildIcon = () => {
    if (displayBlockType === "label_group_category") return "folder"
    if (displayBlockType === "label_group") return "square"
    if (displayBlockType === "label") return "circle"
    return "circle"
  }

  const buildSettingButton = (member: Member, noRule?: boolean) => {
    if (!detailData?.editable) {
      return (
        <Tips
          description="閲覧モードのため、編集できません"
          className=""
          icon={false}
        >
          <FontAwesomeIcon
            icon={['far', 'question-circle']}
            className=""
            fixedWidth
            style={{
              color: "#999999",
              fontSize: "16px",
            }}
          />
        </Tips>
      )
    }
    if (!noRule && !detailData?.memberList.map((w) => w.member_id).includes(member.member_id)) {
      return (
        <Tips
          description="現在この業務を仕分けできるメンバーに含まれていないため、%br%担当者に設定することができません"
          className=""
          icon={false}
        >
          <FontAwesomeIcon
            icon={['fas', 'exclamation-circle']}
            className=""
            fixedWidth
            style={{
              color: "#FF0000",
              fontSize: "16px",
            }}
          />
        </Tips>
      )
    }
    return (
      <Tips
        description={!noRule && !assignedMemberIds?.includes(member.member_id) ? "実績が記録されていますが、担当者に設定されていません" : "設定"}
        className=""
        icon={false}
      >
        <>
          <button
            type="button"
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              padding: "4px",
              color: "#666666",
              backgroundColor: "#FFFFFF",
              position: "relative",
            }}
            onClick={() => openModalCallback({
              blockType: noRule ? "label" : "member",
              id: member.member_id,
              modalType: "config",
              displayType: isDisplayWeekly ? "weekly" : "daily",
              memberIds: noRule ? dataList?.members.map((w) => w.member_id) : undefined,
              labelGroupId: detailData?.labelGroupId,
              parentId: !noRule ? dataList?.id : undefined,
              assignedMemberIds: noRule ? assignedMemberIds : undefined,
            })}
          >
            <FontAwesomeIcon
              icon={['fas', 'cog']}
              className=""
              fixedWidth
            />
            {
              !noRule && !assignedMemberIds?.includes(member.member_id) && (
                <div
                  style={{
                    position: "absolute",
                    top: "-7px",
                    right: "-7px",
                  }}
                >
                  <FontAwesomeIcon
                    icon={['fas', 'exclamation-circle']}
                    className=""
                    fixedWidth
                    style={{
                      color: "#FF0000",
                      fontSize: "12px",
                    }}
                  />
                </div>
              )
            }
          </button>
        </>
      </Tips>
    )
  }

  const buildTitleList = (members: Member[], noRule?: boolean, bgColor?: string):JSX.Element => {
    if (!members.length) return (<></>)
    return (
      <>
        {
          members.map((v) => (
            <React.Fragment key={`task_detail_member_${v.name}_${v.member_id}`}>
              <div
                style={{
                  position: "relative",
                  marginTop: "-1px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1px solid #cccccc",
                  padding: "4px 8px",
                  minHeight: "40px",
                  backgroundColor: detailData?.editable && (assignedMemberIds?.includes(v.member_id) || noRule) ? bgColor : "#EEEEEE",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    maxWidth: `${titleWidth - 50}px`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {
                    !noRule && (
                      <div
                        style={{
                          width: "14px",
                        }}
                      >
                        ┗
                      </div>
                    )
                  }
                  {
                    !noRule ? (
                      <>
                        {
                          v.profile_image ? (
                            <img
                              alt=""
                              src={v.profile_image}
                              style={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            <img
                              alt=""
                              src={`/static/images/profile/${(v.member_id || 0) % 12}.jpeg`}
                              style={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "50%",
                              }}
                            />
                          )
                        }
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={['fas', buildIcon()]}
                          className="icon"
                          style={{
                            color: dataList?.color,
                            fontSize: "14px",
                          }}
                        />
                      </>
                    )
                  }
                  {v.name}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    alignItems: "center",
                  }}
                >
                  {buildSettingButton(v, noRule)}
                </div>
              </div>
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const updateStatus = (memberId: number, status: string | null, callback: VoidFunction) => {
    const target = dataList?.members.find((v) => v.member_id === memberId)
    const params:UpdateParams = {
      blockType: "member",
      id: memberId,
      startDate: target?.schedule.start_date || null,
      endDate: target?.schedule.end_date || null,
      parentId: dataList?.id,
      status: status,
      callback: callback,
    }
    if (data) {
      setData({
        ...data,
        members: data?.members.map((v) => (
          v.member_id === memberId
            ? { ...v, schedule: { ...v.schedule, start_date: target?.schedule.start_date || null, end_date: target?.schedule.end_date || null }, status: status }
            : v
        )) || [],
      })
    }
    updateCallback(params)
  }

  const buildSelectBox = (memberId: number, status: string | null) => {
    const callback = () => {
      setIsStatusLoading(false)
    }
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
        }}
      >
        <select
          style={{
            width: "100%",
            textAlign: "center",
            border: "solid 1px #999999",
            borderRadius: "4px",
            padding: "4px",
            appearance: "auto",
            backgroundColor: SELECT_LIST.find((v) => v.value === status)?.bg || "#FFFFFF",
          }}
          onChange={(e) => {
            setIsStatusLoading(true)
            updateStatus(memberId, e.currentTarget.value, callback)
          }}
          defaultValue={status || ""}
        >
          {
            SELECT_LIST.filter((v) => v.display).map((v) => (
              <option value={v.value || ""}>
                {v.label}
              </option>
            ))
          }
        </select>
      </div>
    )
  }

  const buildStatusList = (members: Member[], isMember: boolean):JSX.Element => {
    if (!members.length) return (<></>)
    return (
      <>
        {
          members.map((v) => (
            <React.Fragment key={`status_${v.name}_${v.member_id}`}>
              <div
                style={{
                  position: "relative",
                  marginTop: "-1px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "1px solid #cccccc",
                  padding: "4px 8px",
                  minHeight: "40px",
                  backgroundColor: (detailData?.editable || currentMemberId === v.member_id) && isMember && assignedMemberIds?.includes(v.member_id) ? "#FFFFFF" : "#EEEEEE",
                }}
              >
                {
                  (detailData?.editable || currentMemberId === v.member_id) && isMember && assignedMemberIds?.includes(v.member_id) ? (
                    <>
                      {buildSelectBox(v.member_id, v.status)}
                    </>
                  ) : (
                    <>
                      {SELECT_LIST.find((w) => w.value === v.status)?.label || "-"}
                    </>
                  )
                }
              </div>
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const buildBgColor = (color?: string, blockType?: string) => {
    const alpha = blockType === "member" ? 0.5 : 1
    return convertHexColor(color || "#FFFFFF", alpha)
  }

  const updateSchedule = (blockType: string, id: number, startDate: string, endDate: string, callback: VoidFunction, parentId?: number) => {
    const params:UpdateParams = {
      blockType: blockType,
      id: id,
      startDate: startDate,
      endDate: endDate,
      memberIds: assignedMemberIds,
      callback: callback,
    }
    if (blockType === "member") {
      params.parentId = parentId
      if (data) {
        setData({
          ...data,
          members: data?.members.map((v) => (
            v.member_id === id ? { ...v, schedule: { ...v.schedule, start_date: startDate, end_date: endDate } } : v
          )) || [],
        })
      }
    } else if (blockType === "label") {
      params.parentId = detailData?.labelGroupId
      if (data) {
        setData({
          ...data,
          schedule: {
            ...data.schedule,
            start_date: startDate,
            end_date: endDate,
          },
        })
      }
    }
    updateCallback(params)
  }

  const buildScheduleList = (values: PackageBlock[], bgColor?: string, parent?: {blockType: string, id: number | undefined, color: string}):JSX.Element => {
    if (!values.length) return (<></>)
    return (
      <>
        {
          values.map((v) => (
            <React.Fragment key={`detail_scheduleList_${v.block_type}_${v.id}`}>
              <GanttChartScheduleList
                packageBlock={v}
                displayDateList={displayDateList}
                updateCallback={
                  (blockType, id, startDate, endDate, callback) => updateSchedule(
                    blockType,
                    id,
                    startDate,
                    endDate,
                    callback,
                    parent?.id,
                  )
                }
                openModalCallback={(blockType, id) => openModalCallback({
                  blockType: blockType,
                  id: id,
                  modalType: "config",
                  displayType: isDisplayWeekly ? "weekly" : "daily",
                  memberIds: v.block_type === "label" ? v.assigned_members.map((w) => w.member_id) : undefined,
                  labelGroupId: v.block_type === "label" ? parent?.id : undefined,
                  parentId: v.block_type === "member" ? parent?.id : undefined,
                  assignedMemberIds: v.block_type === "label" ? assignedMemberIds : undefined,
                })}
                displayType={isDisplayWeekly ? "weekly" : "daily"}
                displayBlockWidth={isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH}
                bgColor={buildBgColor(bgColor || v.color, v.block_type)}
                editable={detailData?.editable && (assignedMemberIds?.includes(v.id) || !parent)}
              />
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const buildSecToTime = (sec: number, min?: boolean, text?: boolean) => {
    if (text) {
      const hour = Number(secToHour(sec)) ? `${secToHour(sec)}時間` : ""
      const minute = Number(secToMinute(sec)) ? `${secToMinute(sec)}分` : ""
      const second = Number(secToSec(sec)) ? `${secToSec(sec)}秒` : ""
      return min ? `${hour}` : `${hour}${minute}${second}`
    }
    return (
      <>
        {
          !!Number(secToHour(sec)) && (
            <>
              {`${secToHour(sec)}時間`}
            </>
          )
        }
        {
          !min && (
            <>
              {
                !!Number(secToMinute(sec)) && (
                  <>
                    {`${secToMinute(sec)}分`}
                  </>
                )
              }
              {
                !!Number(secToSec(sec)) && (
                  <>
                    {`${secToSec(sec)}秒`}
                  </>
                )
              }
            </>
          )
        }
      </>
    )
  }

  const buildCostList = (values: PackageBlock[]):JSX.Element => {
    if (!values.length) return (<></>)
    return (
      <>
        {
          values.map((v) => (
            <React.Fragment key={`cost_${v.block_type}_${v.id}`}>
              <div
                style={{
                  display: "flex",
                  flexFlow: "column",
                  gap: "4px",
                  padding: "2px 4px",
                  height: "40px",
                  marginTop: "-1px",
                  border: "1px solid #dddddd",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "10px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      borderRadius: "4px",
                      backgroundColor: "#6fcdcd",
                      color: "#FFFFFF",
                      padding: "2px 4px",
                    }}
                  >
                    予定工数
                  </div>
                  <div
                    style={{
                      color: "#666666",
                    }}
                  >
                    {buildSecToTime(v.schedule.time || 0, true)}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "10px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      borderRadius: "4px",
                      backgroundColor: "#cc6868",
                      color: "#FFFFFF",
                      padding: "2px 4px",
                    }}
                  >
                    実績工数
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      color: "#666666",
                    }}
                  >
                    {buildSecToTime(v.actual.reduce((s, x) => s + x.time, 0) || 0, true)}
                    {
                      !!v.schedule.time && (v.actual.reduce((s, x) => s + x.time, 0) || 0) - v.schedule.time >= 3600 && (
                        <Tips
                          description={`予定より+${buildSecToTime((v.actual.reduce((s, x) => s + x.time, 0) || 0) - v.schedule.time, true, true)}`}
                          className=""
                          icon={false}
                        >
                          <FontAwesomeIcon
                            icon={['fas', 'exclamation-circle']}
                            className=""
                            fixedWidth
                            style={{
                              color: "#FF0000",
                              fontSize: "12px",
                            }}
                          />
                        </Tips>
                      )
                    }
                  </div>
                </div>
              </div>
              {buildCostList(v.children || [])}
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const todayBorder = () => {
    let startPosition = 0
    if (
      !(
        Number(format(displayDateList[0], "YYYYMMDD")) < Number(nowDate("YYYYMMDD"))
        && Number(format(displayDateList[displayDateList.length - 1], "YYYYMMDD")) > Number(nowDate("YYYYMMDD"))
      )
    ) return (<></>)
    if (!isDisplayWeekly) {
      startPosition = diffDays(displayDateList[0], nowDate()) * (DAILY_BLOCK_WIDTH + 1)
    } else {
      startPosition = (diffDays(displayDateList[0], nowDate()) / 7) * (WEEKLY_BLOCK_WIDTH + 1)
    }
    return (
      <>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: `${startPosition}px`,
            width: "1px",
            height: `${listCount * 39}px`,
            backgroundColor: "#FF0000",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "-14px",
            left: `${startPosition + 0.5}px`,
            zIndex: 1,
            fontSize: "16px",
            transform: "translateX(-50%)",
            color: "#FF0000",
          }}
        >
          <Tips
            description="本日"
            className="bottom"
            icon={false}
          >
            <>
              <FontAwesomeIcon icon={['fas', 'caret-down']} />
            </>
          </Tips>
        </div>
      </>
    )
  }

  const buildYMD = (date: string) => {
    if (!date) return (<></>)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "1px",
          fontSize: "10px",
          color: "#333333",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          {format(date, "YYYY")}
        </div>
        年
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          {format(date, "M")}
        </div>
        月
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          {format(date, "D")}
        </div>
        日
      </div>
    )
  }

  useEffect(() => {
    setIsDisplayWeekly(displayType === "weekly")
  }, [displayType])

  useEffect(() => {
    setData(dataList)
  }, [dataList])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="gantt_modal w1100"
      closeCallback={closeModal}
      closeable
    >
      <>
        {
          isLoading ? (
            <>
              <LoadingSquare>
                <>
                  詳細情報を取得しています…
                </>
              </LoadingSquare>
            </>
          ) : (
            <>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "4px 8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginBottom: "16px",
                  color: "#666666",
                }}
              >
                <FontAwesomeIcon
                  icon={['fas', buildIcon()]}
                  className="icon"
                  style={{
                    color: data?.color,
                    fontSize: "14px",
                  }}
                />
                {`${data?.name} 詳細情報`}
              </div>
              <div
                style={{
                  border: "solid 1px #CCCCCC",
                  padding: "8px",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      width: "50%",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#219c0a",
                        color: "#FFFFFF",
                        borderRadius: "4px",
                        fontSize: "9px",
                        padding: "2px 4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={['fas', "clock"]}
                        className=""
                      />
                      予定期間
                    </div>
                    <div>
                      {
                        data?.schedule.start_date ? (
                          <>
                            {data.schedule.start_date}
                          </>
                        ) : (
                          <>
                            指定なし
                          </>
                        )
                      }
                      ~
                      {
                        data?.schedule.end_date ? (
                          <>
                            {data.schedule.end_date}
                          </>
                        ) : (
                          <>
                            指定なし
                          </>
                        )
                      }
                    </div>
                  </div>
                  <div
                    style={{
                      width: "50%",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {
                      !!data?.schedule.time && (
                        <>
                          <div
                            style={{
                              backgroundColor: "#219c0a",
                              color: "#FFFFFF",
                              borderRadius: "4px",
                              fontSize: "9px",
                              padding: "2px 4px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={['fas', "hammer"]}
                              className=""
                            />
                            予定工数
                          </div>
                          <div>
                            {buildSecToTime(data.schedule.time)}
                          </div>
                        </>
                      )
                    }
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      width: "50%",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#b21c1c",
                        color: "#FFFFFF",
                        borderRadius: "4px",
                        fontSize: "9px",
                        padding: "2px 4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={['fas', "clock"]}
                        className=""
                      />
                      実績期間
                    </div>
                    <div>
                      {
                        data?.min_date ? (
                          <>
                            {data.min_date}
                          </>
                        ) : (
                          <>
                            なし
                          </>
                        )
                      }
                      ~
                      {
                        data?.max_date ? (
                          <>
                            {data.max_date}
                          </>
                        ) : (
                          <>
                            なし
                          </>
                        )
                      }
                    </div>
                  </div>
                  <div
                    style={{
                      width: "50%",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {
                      !!data?.actual.length && (
                        <>
                          <div
                            style={{
                              backgroundColor: "#b21c1c",
                              color: "#FFFFFF",
                              borderRadius: "4px",
                              fontSize: "9px",
                              padding: "2px 4px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={['fas', "hammer"]}
                              className=""
                            />
                            実績工数
                          </div>
                          <div>
                            {buildSecToTime(data.actual.reduce((s, v) => s + v.time, 0))}
                          </div>
                        </>
                      )
                    }
                  </div>
                </div>
                <hr
                  style={{
                    color: "#CCCCCC",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      width: "calc(100% / 3)",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#0b93cb",
                        color: "#FFFFFF",
                        borderRadius: "4px",
                        fontSize: "9px",
                        padding: "2px 4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={['fas', "users"]}
                        className=""
                      />
                      総参加人数
                    </div>
                    <div>
                      {`${data?.members.length}人`}
                    </div>
                  </div>
                  <div
                    style={{
                      width: "calc(100% / 3)",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#de7307",
                        color: "#FFFFFF",
                        borderRadius: "4px",
                        fontSize: "9px",
                        padding: "2px 4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={['fas', "user-clock"]}
                        className=""
                      />
                      一人当たりの平均実績工数
                    </div>
                    <div>
                      {buildSecToTime((data?.actual.reduce((s, v) => s + v.time, 0) || 0) / (data?.members.length || 1))}
                    </div>
                  </div>
                  <div
                    style={{
                      width: "calc(100% / 3)",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#de7307",
                        color: "#FFFFFF",
                        borderRadius: "4px",
                        fontSize: "9px",
                        padding: "2px 4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={['fas', "calendar-check"]}
                        className=""
                      />
                      一日当たりの平均実績工数
                    </div>
                    <div>
                      {buildSecToTime((data?.actual.reduce((s, v) => s + v.time, 0) || 0) / (data?.actual.length || 1))}
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    padding: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  メンバー別ガントビュー
                </div>
                <div
                  className="button_area"
                  key={`gantt_chart_switch_${isDisplayWeekly}`}
                  style={{
                    width: "140px",
                  }}
                >
                  {
                    isDisplayWeekly ? (
                      <>
                        <Tips
                          description="日表示に切り替えます"
                          className=""
                          icon={false}
                        >
                          <>
                            <button
                              type="button"
                              onClick={() => setIsDisplayWeekly(false)}
                              style={{
                                border: "1px solid #cccccc",
                                borderRadius: "4px",
                                padding: "4px 6px",
                                display: "flex",
                                gap: "4px",
                                fontSize: "12px",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'calendar-week']} />
                              週表示
                              <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                              <FontAwesomeIcon icon={['fas', 'calendar-day']} />
                              日表示
                            </button>
                          </>
                        </Tips>
                      </>
                    ) : (
                      <>
                        <Tips
                          description="週表示に切り替えます"
                          className=""
                          icon={false}
                        >
                          <>
                            <button
                              type="button"
                              onClick={() => setIsDisplayWeekly(true)}
                              style={{
                                border: "1px solid #cccccc",
                                borderRadius: "4px",
                                padding: "4px 6px",
                                display: "flex",
                                gap: "4px",
                                fontSize: "12px",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'calendar-day']} />
                              日表示
                              <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                              <FontAwesomeIcon icon={['fas', 'calendar-week']} />
                              週表示
                            </button>
                          </>
                        </Tips>
                      </>
                    )
                  }
                </div>
              </div>
              {
                !detailData?.editable && (
                  <div
                    style={{
                      backgroundColor: "#ffdfdf",
                      padding: "8px",
                      color: "#c20000",
                      marginBottom: "8px",
                    }}
                  >
                    編集権限がないため、閲覧モードでの表示になります。また、実績は自身の実績のみ表示されます。
                  </div>
                )
              }
              <div
                style={{
                  position: "relative",
                }}
              >
                {
                  isStatusLoading && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        zIndex: 3,
                      }}
                    >
                      <LoadingSquare>
                        <>
                        </>
                      </LoadingSquare>
                    </div>
                  )
                }
                <div
                  style={{
                    position: "relative",
                    border: "solid 1px #CCCCCC",
                    marginTop: "-1px",
                    marginBottom: "24px",
                    padding: "8px",
                    maxHeight: "350px",
                    overflowY: isStatusLoading ? "hidden" : "scroll",
                  }}
                >
                  {
                    data ? (
                      <React.Fragment key="gantt_chart_area">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            backgroundColor: "#EEEEEE",
                            padding: "4px",
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              border: "1px solid #CCCCCC",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "12px",
                              backgroundColor: "#ffffff",
                            }}
                            onClick={() => setAddMonthNum(addMonthNum - 1)}
                          >
                            <FontAwesomeIcon icon={['fas', 'caret-left']} />
                            1ヶ月前
                          </button>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: "4px",
                            }}
                          >
                            表示期間:
                            {buildYMD(displayDateList[0])}
                            ~
                            {
                              isDisplayWeekly ? (
                                <>
                                  {buildYMD(dateManipulateDay(displayDateList[displayDateList.length - 1], 6))}
                                </>
                              ) : (
                                <>
                                  {buildYMD(displayDateList[displayDateList.length - 1])}
                                </>
                              )
                            }
                          </div>
                          <button
                            type="button"
                            style={{
                              border: "1px solid #CCCCCC",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "12px",
                              backgroundColor: "#ffffff",
                            }}
                            onClick={() => setAddMonthNum(addMonthNum + 1)}
                          >
                            1ヶ月後
                            <FontAwesomeIcon icon={['fas', 'caret-right']} />
                          </button>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            position: "relative",
                          }}
                        >
                          <div
                            ref={titleRef}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "40px",
                              zIndex: 1,
                            }}
                            onMouseMove={(e) => isMoveDrag && setTitleWidth(e.clientX - titleRef.current.getBoundingClientRect().left)}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "12px",
                                left: `${titleWidth}px`,
                                cursor: "col-resize",
                                transform: "translateX(-50%)",
                                fontSize: "16px",
                                color: "#EEEEEE",
                              }}
                              onMouseDown={() => setIsMoveDrag(true)}
                              onMouseUp={() => setIsMoveDrag(false)}
                              role="none"
                            >
                              <FontAwesomeIcon icon={['fas', 'grip-lines-vertical']} />
                            </div>
                          </div>
                          <div
                            style={{
                              width: `${titleWidth}px`,
                            }}
                          >
                            <div
                              style={{
                                padding: "4px",
                                textAlign: "center",
                                fontWeight: "bold",
                                minHeight: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            />
                            {buildTitleList([{ member_id: data.id, name: data.name, profile_image: null, code: null, kana: "", schedule: data.schedule, actual: data.actual, status: null }], true, detailData?.bgColor)}
                            {buildTitleList(data.members, false, convertHexColor(detailData?.bgColor || "#FFFFFF", 0.5))}
                          </div>
                          <div
                            style={{
                              width: `${STATUS_WIDTH}px`,
                              height: `${(listCount * 39) + 1}px`,
                              display: "flex",
                              flexFlow: "column",
                              backgroundColor: "#FFFFFF",
                              marginLeft: "-1px",
                            }}
                          >
                            <div
                              style={{
                                padding: "4px",
                                textAlign: "center",
                                fontWeight: "bold",
                                minHeight: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                userSelect: "none",
                              }}
                            >
                              ステータス
                            </div>
                            {buildStatusList([{ member_id: data.id, name: data.name, profile_image: null, code: null, kana: "", schedule: data.schedule, actual: data.actual, status: null }], false)}
                            {buildStatusList(data.members, true)}
                          </div>
                          <div
                            style={{
                              width: `calc(100% - ${titleWidth + STATUS_WIDTH}px)`,
                              overflowX: "scroll",
                              scrollbarWidth: "none",
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                position: "relative",
                                width: "fit-content",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: "1px",
                                  minHeight: "40px",
                                  fontWeight: "bold",
                                  fontSize: "10px",
                                }}
                              >
                                {
                                  displayDateList.map((v, i) => (
                                    <div
                                      key={`date_list_${v}`}
                                      style={{
                                        color: isDisplayWeekly ? "#666666" : calendarColor(v),
                                        width: `${isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH}px`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "8px 4px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {
                                        isDisplayWeekly ? (
                                          <>
                                            {`${format(v, "M/D")} - ${dateManipulateDay(v, 6, "M/D")}`}
                                          </>
                                        ) : (
                                          <>
                                            {
                                              !i || Number(format(v, "D")) === 1 ? (
                                                <>
                                                  {format(v, "M/D")}
                                                </>
                                              ) : (
                                                <>
                                                  {format(v, "D")}
                                                </>
                                              )
                                            }
                                            {
                                              format(v, "YYYYMMDD") === yesterDay("YYYYMM01") && (
                                                <div ref={dateRef} />
                                              )
                                            }
                                          </>
                                        )
                                      }
                                    </div>
                                  ))
                                }
                              </div>
                              <div
                                style={{
                                  position: "relative",
                                  width: `${(displayDateList.length * ((isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH) + 1) + 1)}px`,
                                  height: `${(listCount * 39) + 1}px`,
                                  border: "solid 1px #CCCCCC",
                                  marginTop: "-1px",
                                }}
                              >
                                {
                                  displayDateList.map((v, i) => (
                                    <React.Fragment key={`detail_data_list_${v}_${displayBlockType}_${data.id}`}>
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: `${((isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH) + 1) * i}px`,
                                          width: "1px",
                                          height: `${listCount * 39}px`,
                                          backgroundColor: "#DDDDDD",
                                          zIndex: 1,
                                        }}
                                      />
                                      {
                                        !isDisplayWeekly && calendarColor(v) !== "#666666" && (
                                          <div
                                            style={{
                                              position: "absolute",
                                              left: `${((isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH) + 1) * i}px`,
                                              width: `${((isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH) + 1)}px`,
                                              height: `${listCount * 39}px`,
                                              backgroundColor: `${calendarColor(v)}0d`,
                                              pointerEvents: "none",
                                              zIndex: 1,
                                            }}
                                          />
                                        )
                                      }
                                    </React.Fragment>
                                  ))
                                }
                                {buildScheduleList([{ ...data, block_type: displayBlockType, assigned_members: data.members, children: [] }], detailData?.bgColor)}
                                {buildScheduleList(data.members.map((v) => ({ ...v, id: v.member_id, block_type: "member", color: "#FFFFFF", assigned_members: [], children: [] })), detailData?.bgColor, { blockType: displayBlockType, id: data.id, color: data.color })}
                                {todayBorder()}
                              </div>
                            </div>
                          </div>
                          {/* {
                            !isMoving && (
                              <>
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "40px",
                                    right: 0,
                                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                                    width: "140px",
                                    height: `${(listCount * 39) + 1}px`,
                                    zIndex: 2,
                                    display: "flex",
                                    flexFlow: "column",
                                  }}
                                >
                                  {buildCostList([{ ...data, actual: [], block_type: displayBlockType, assigned_members: [], children: [] }, ...data.members.map((v) => ({ ...v, id: v.member_id, block_type: "member", color: "#FFFFFF", assigned_members: [], children: [] }))])}
                                </div>
                              </>
                            )
                          } */}
                        </div>
                      </React.Fragment>
                    ) : (
                      <>
                        <div
                          style={{
                            textAlign: "center",
                            fontSize: "30px",
                            color: "#CCCCCC",
                          }}
                        >
                          対象のデータがありませんでした
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
              <div className="button_area">
                <button
                  type="button"
                  className="button cancel"
                  onClick={() => closeCallback()}
                >
                  閉じる
                </button>
              </div>
            </>
          )
        }
      </>
    </Modal>
  )
}

GanttDetailModal.defaultProps = {
  assignedMemberIds: [],
}

export default GanttDetailModal
